import React, { useEffect, useState, useRef } from 'react';
import api from '../api/axios';
import { useNotification } from './NotificationContext';

export default function ZapierIntegration() {
  const { showNotification, removeNotification } = useNotification();
  const [webhooks, setWebhooks] = useState([]);
  const [triggers, setTriggers] = useState([]);

  // unified “Create” dropdown state
  const [openCreate, setOpenCreate] = useState(false);
  const dropdownRef = useRef(null);

  const [creatingToken, setCreatingToken] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);

  const [newTrig, setNewTrig] = useState({ label: '', eventType: 'new_message', targetUrl: '', filtersJson: '{}' });
  const [showTriggerForm, setShowTriggerForm] = useState(false);
  const [creatingTrig, setCreatingTrig] = useState(false);

  const load = async () => {
    try {
      const [wh, tr] = await Promise.all([
        api.get('/integrations/zapier/zapier/webhooks'),
        api.get('/integrations/zapier/zapier/triggers'),
      ]);
      setWebhooks(wh.data.webhooks || []);
      setTriggers(tr.data.triggers || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setOpenCreate(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // ----- Create actions -----
  const createToken = async () => {
    setCreatingToken(true);
    try {
      const res = await api.post('/integrations/zapier/zapier/webhooks', { label: 'Zapier' });
      setCreatedToken(res.data);
      const id = showNotification('Zapier inbound token created', 'success'); setTimeout(() => removeNotification(id), 4000);
      await load();
    } catch (e) {
      const id = showNotification('Failed to create token', 'danger'); setTimeout(() => removeNotification(id), 4000);
    } finally {
      setCreatingToken(false);
      setOpenCreate(false);
    }
  };

  const createTrigger = async () => {
    setCreatingTrig(true);
    try {
      let filters = {};
      if (newTrig.filtersJson.trim()) filters = JSON.parse(newTrig.filtersJson);
      const res = await api.post('/integrations/zapier/zapier/triggers', {
        label: newTrig.label || 'Zapier Trigger',
        eventType: newTrig.eventType,
        targetUrl: newTrig.targetUrl,
        filters,
      });
      alert(`Save this secret now:\n\n${res.data.secret}\n\nYou need it to verify webhooks in Zapier.`);
      const id = showNotification('Trigger created', 'success'); setTimeout(() => removeNotification(id), 4000);
      setNewTrig({ label: '', eventType: 'new_message', targetUrl: '', filtersJson: '{}' });
      setShowTriggerForm(false);
      await load();
    } catch (e) {
      const id = showNotification(e?.message || 'Failed to create trigger', 'danger'); setTimeout(() => removeNotification(id), 4000);
    } finally {
      setCreatingTrig(false);
      setOpenCreate(false);
    }
  };

  // ----- State updates (revoke/disable) -----
  const revokeWebhook = async (id) => { // enabled -> false
    if (!window.confirm('Revoke this token?')) return;
    await api.patch(`/integrations/zapier/zapier/webhooks/${id}`, { enabled: false });
    await load();
  };

  const deleteWebhook = async (id) => {
    if (!window.confirm('Delete this revoked token permanently?')) return;
    await api.delete(`/integrations/zapier/zapier/webhooks/${id}`);
    await load();
  };

  const disableTrigger = async (id) => {
    if (!window.confirm('Disable this trigger?')) return;
    await api.patch(`/integrations/zapier/zapier/triggers/${id}`, { enabled: false });
    await load();
  };

  const deleteTrigger = async (id) => {
    if (!window.confirm('Delete this disabled trigger?')) return;
    await api.delete(`/integrations/zapier/zapier/triggers/${id}`);
    await load();
  };

  // ----- Helpers -----
  const buildUrl = (inboundPath, inboundUrlFromServer) => {
    if (inboundUrlFromServer) return inboundUrlFromServer;
    if (!inboundPath) return '';
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '');
    return `${base}${inboundPath}`;
  };
  const lastCreatedInboundUrl = buildUrl(createdToken?.inboundPath, createdToken?.inboundUrl);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    const id = showNotification('Copied to clipboard', 'success'); setTimeout(() => removeNotification(id), 2000);
  };

  const enabledWebhooks = webhooks.filter(w => w.enabled);
  const revokedWebhooks = webhooks.filter(w => !w.enabled);
  const enabledTriggers = triggers.filter(t => t.enabled);
  const disabledTriggers = triggers.filter(t => !t.enabled);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold">Zapier</h3>
          <p className="text-sm text-gray-600">Create inbound tokens for Actions and outbound Triggers for Zapier Catch Hooks.</p>
        </div>

        {/* Create dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpenCreate(v => !v)}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700"
          >
            <i className="fas fa-plus" /> Create
            <i className={`fas fa-chevron-down transition ${openCreate ? 'rotate-180' : ''}`} />
          </button>
          {openCreate && (
            <div className="absolute right-0 mt-2 w-56 bg-white border rounded-md shadow-lg z-10 overflow-hidden">
              <button
                onClick={createToken}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                disabled={creatingToken}
              >
                <i className="fas fa-key" /> {creatingToken ? 'Creating…' : 'Inbound action token'}
              </button>
              <button
                onClick={() => { setShowTriggerForm(true); setOpenCreate(false); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <i className="fas fa-bolt" /> Outbound trigger
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Flash (shown-once) created token */}
      {createdToken && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
          <div className="font-medium mb-1">Copy now (shown once):</div>
          <div className="text-xs break-all space-y-1">
            <div className="flex items-center gap-2">
              <b>Inbound URL:</b> <code className="break-all">{lastCreatedInboundUrl}</code>
              <button className="ml-auto border px-2 py-0.5 rounded text-xs" onClick={() => copy(lastCreatedInboundUrl)}>Copy</button>
            </div>
            <div className="flex items-center gap-2">
              <b>Token:</b> <code>{createdToken.token}</code> <span className="text-gray-500">(…{createdToken.last4})</span>
              <button className="ml-auto border px-2 py-0.5 rounded text-xs" onClick={() => copy(createdToken.token)}>Copy</button>
            </div>
          </div>
        </div>
      )}

      {/* Inbound (enabled + revoked) */}
      <div className="border rounded-lg p-4 mb-6 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Inbound Actions (Zapier → App)</h4>
        </div>

        {/* Enabled */}
        <div className="space-y-2">
          {enabledWebhooks.length === 0 && revokedWebhooks.length === 0 && (
            <div className="text-sm text-gray-500">No tokens yet. Use <b>Create</b> to add one.</div>
          )}

          {enabledWebhooks.map(w => (
            <div key={w._id} className="border p-3 rounded flex items-start justify-between">
              <div className="text-sm">
                <div className="font-medium">
                  {w.label} <span className="text-gray-500">…{w.last4}</span>
                  <span className="ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Active</span>
                </div>
                <div className="text-gray-500">Default user: {w.defaultUserEmail || '-'}</div>
                <div className="text-gray-500">Created {new Date(w.createdAt).toLocaleString()}</div>
                {w.lastUsedAt && <div className="text-gray-500">Last used {new Date(w.lastUsedAt).toLocaleString()}</div>}
              </div>
              <div className="flex gap-2">
                <button className="text-red-600 border border-red-600 px-3 py-1 rounded text-sm" onClick={() => revokeWebhook(w._id)}>
                  Revoke
                </button>
              </div>
            </div>
          ))}

          {/* Revoked list with delete */}
          {revokedWebhooks.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm text-gray-700">Revoked ({revokedWebhooks.length})</summary>
              <div className="mt-2 space-y-2">
                {revokedWebhooks.map(w => (
                  <div key={w._id} className="border p-3 rounded flex items-start justify-between bg-gray-50">
                    <div className="text-sm">
                      <div className="font-medium">
                        {w.label} <span className="text-gray-500">…{w.last4}</span>
                        <span className="ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Revoked</span>
                      </div>
                      <div className="text-gray-500">Created {new Date(w.createdAt).toLocaleString()}</div>
                      {w.lastUsedAt && <div className="text-gray-500">Last used {new Date(w.lastUsedAt).toLocaleString()}</div>}
                    </div>
                    <button className="text-red-600 border border-red-600 px-3 py-1 rounded text-sm" onClick={() => deleteWebhook(w._id)}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>

      {/* Triggers */}
      <div className="border rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Outbound Triggers (App → Zapier)</h4>
        </div>

        {/* Create trigger (toggle form) */}
        {showTriggerForm && (
          <div className="border rounded p-3 bg-gray-50 mb-4">
            <div className="grid gap-2 md:grid-cols-3">
              <input className="border rounded px-2 py-1 text-sm" placeholder="Label"
                    value={newTrig.label} onChange={e=>setNewTrig(s=>({...s,label:e.target.value}))}/>
              <select className="border rounded px-2 py-1 text-sm"
                      value={newTrig.eventType} onChange={e=>setNewTrig(s=>({...s,eventType:e.target.value}))}>
                <optgroup label="Lead & campaign">
                  <option value="new_connection">Connection accepted</option>
                  <option value="new_message">New inbound reply</option>
                  <option value="lead_qualified">Lead qualified</option>
                  <option value="campaign_completed">Campaign completed</option>
                </optgroup>
                <optgroup label="Meetings">
                  <option value="meeting_booked">Meeting booked</option>
                  <option value="meeting_rescheduled">Meeting rescheduled</option>
                  <option value="meeting_cancelled">Meeting cancelled</option>
                  <option value="meeting_completed">Meeting completed</option>
                  <option value="meeting_no_show">Meeting no-show</option>
                </optgroup>
                <optgroup label="Agent Mode">
                  <option value="agent_action_proposed">Agent action proposed</option>
                  <option value="agent_action_approved">Agent action approved</option>
                  <option value="agent_action_rejected">Agent action rejected</option>
                  <option value="agent_action_completed">Agent action completed</option>
                  <option value="agent_action_failed">Agent action failed</option>
                </optgroup>
              </select>
              <input className="border rounded px-2 py-1 text-sm" placeholder="Zapier Catch Hook URL (https)"
                    value={newTrig.targetUrl} onChange={e=>setNewTrig(s=>({...s,targetUrl:e.target.value}))}/>
            </div>
            <textarea className="border rounded px-2 py-1 text-xs w-full mt-2" rows={3}
                    placeholder='Optional filters JSON, e.g. { "payload.campaignId": "..." }'
                    value={newTrig.filtersJson} onChange={e=>setNewTrig(s=>({...s,filtersJson:e.target.value}))}/>
            <div className="mt-2 flex gap-2">
              <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm" onClick={createTrigger} disabled={creatingTrig}>
                {creatingTrig ? 'Creating…' : 'Create trigger'}
              </button>
              <button className="border px-3 py-1 rounded text-sm" onClick={()=>{ setShowTriggerForm(false); setNewTrig({ label:'', eventType:'new_message', targetUrl:'', filtersJson:'{}' }); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Enabled triggers */}
        <div className="space-y-2">
          {enabledTriggers.length === 0 && disabledTriggers.length === 0 && (
            <div className="text-sm text-gray-500">No triggers yet. Use <b>Create</b> to add one.</div>
          )}

          {enabledTriggers.map(t => (
            <div key={t._id} className="border rounded p-3 flex items-start justify-between">
              <div className="text-sm">
                <div className="font-medium">
                  {t.label} <span className="text-gray-500">({t.eventType})</span>
                  <span className="ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">Active</span>
                </div>
                <div className="text-gray-500 break-all">{t.targetUrl}</div>
                <div className="text-gray-500">Created {new Date(t.createdAt).toLocaleString()}</div>
                {t.lastUsedAt && <div className="text-gray-500">Last fired {new Date(t.lastUsedAt).toLocaleString()}</div>}
              </div>
              <button className="text-red-600 border border-red-600 px-3 py-1 rounded text-sm" onClick={()=>disableTrigger(t._id)}>
                Disable
              </button>
            </div>
          ))}

          {/* Disabled triggers with delete */}
          {disabledTriggers.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm text-gray-700">Disabled ({disabledTriggers.length})</summary>
              <div className="mt-2 space-y-2">
                {disabledTriggers.map(t => (
                  <div key={t._id} className="border rounded p-3 flex items-start justify-between bg-gray-50">
                    <div className="text-sm">
                      <div className="font-medium">
                        {t.label} <span className="text-gray-500">({t.eventType})</span>
                        <span className="ml-2 inline-flex items-center text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">Disabled</span>
                      </div>
                      <div className="text-gray-500 break-all">{t.targetUrl}</div>
                      <div className="text-gray-500">Created {new Date(t.createdAt).toLocaleString()}</div>
                    </div>
                    <button className="text-red-600 border border-red-600 px-3 py-1 rounded text-sm" onClick={()=>deleteTrigger(t._id)}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
